import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const PHONE_INTL = "38345813843"; // wa.me format, no +
const PHONE_DISPLAY = "+383 45 813 843";

const WHATSAPP_TEXTS = {
  de: {
    label: "WhatsApp",
    open: "Chat öffnen",
    close: "Schließen",
    heading: "Chat mit uns",
    sub: "Antwort in der Regel innerhalb einer Stunde.",
    agent: "Arian Isufi",
    agentRole: "ArStudio Solutions LLC",
    greet: "Hallo 👋 Wie können wir Ihnen bei Ihrem Bauprojekt helfen?",
    presetLabel: "Schnellauswahl",
    presets: [
      "Ich benötige ein Angebot für die Ausführungsplanung.",
      "Ich habe Interesse an TGA- oder Elektroplanung.",
      "Ich möchte eine 3D-Visualisierung / Interior Design.",
    ],
    cta: "Auf WhatsApp fortfahren",
    online: "Online",
  },
  en: {
    label: "WhatsApp",
    open: "Open chat",
    close: "Close",
    heading: "Chat with us",
    sub: "We usually reply within an hour.",
    agent: "Arian Isufi",
    agentRole: "ArStudio Solutions LLC",
    greet: "Hi 👋 How can we help with your construction project?",
    presetLabel: "Quick pick",
    presets: [
      "I need a quote for construction planning.",
      "I'm interested in MEP or electrical planning.",
      "I'd like a 3D visualization / interior design.",
    ],
    cta: "Continue on WhatsApp",
    online: "Online",
  },
};

const buildWaLink = (text) => {
  const encoded = encodeURIComponent(text);
  return `https://wa.me/${PHONE_INTL}?text=${encoded}`;
};

const WhatsAppIcon = ({ size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.501-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
    <path d="M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.334.101 11.892c0 2.096.549 4.14 1.595 5.945L0 24l6.335-1.652c1.746.943 3.71 1.444 5.71 1.447h.006c6.585 0 11.946-5.336 11.949-11.896 0-3.176-1.24-6.165-3.495-8.411l.015-.039zM12.05 21.785h-.005a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.975.999-3.648-.235-.374a9.86 9.86 0 01-1.511-5.26c0-5.451 4.446-9.884 9.898-9.884 2.641 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.892 6.994c-.003 5.452-4.437 9.891-9.893 9.891z" />
  </svg>
);

const WhatsAppChat = () => {
  const { lang } = useLang();
  const t = WHATSAPP_TEXTS[lang] || WHATSAPP_TEXTS.de;
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 1200);
    return () => clearTimeout(timer);
  }, []);

  if (!mounted) return null;

  const handlePreset = (msg) => {
    window.open(buildWaLink(msg), "_blank", "noopener,noreferrer");
  };

  return (
    <>
      {/* Panel */}
      {open && (
        <div
          data-testid="whatsapp-panel"
          className="fixed bottom-24 right-4 md:right-8 z-[60] w-[calc(100vw-2rem)] md:w-[380px] max-w-[380px] bg-[#1c1c1e] border border-white/10 shadow-2xl animate-fade-up"
          style={{ animationDuration: "0.4s" }}
        >
          {/* Header */}
          <div className="bg-[#075E54] text-white px-5 py-4 flex items-center gap-3 relative">
            <div className="h-11 w-11 rounded-full bg-[#25D366] flex items-center justify-center flex-shrink-0">
              <WhatsAppIcon size={22} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display font-extrabold text-base leading-tight">
                {t.agent}
              </p>
              <p className="font-mono text-[9px] tracking-[0.25em] text-white/70 flex items-center gap-2 mt-1">
                <span className="h-1.5 w-1.5 rounded-full bg-[#25D366] animate-pulse" />
                {t.online} · {t.agentRole}
              </p>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="whatsapp-close"
              className="text-white/80 hover:text-white transition-colors"
              aria-label={t.close}
            >
              <X size={18} />
            </button>
          </div>

          {/* Body */}
          <div className="bg-[#0f0f10] px-5 py-6 relative">
            <div
              className="absolute inset-0 opacity-[0.03] pointer-events-none"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 0L0 20L20 40L40 20Z' fill='none' stroke='white' stroke-width='0.5'/%3E%3C/svg%3E\")",
              }}
            />
            {/* incoming bubble */}
            <div className="relative flex items-start gap-2 mb-4">
              <div className="max-w-[85%] bg-[#1f1f21] border border-white/5 px-4 py-3 rounded-tr-2xl rounded-br-2xl rounded-bl-2xl">
                <p className="text-white text-sm leading-relaxed">{t.greet}</p>
                <p className="font-mono text-[9px] tracking-[0.2em] text-white/40 mt-2 text-right">
                  {new Date().toLocaleTimeString(lang === "de" ? "de-DE" : "en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
              </div>
            </div>

            {/* Presets */}
            <div className="relative mt-6">
              <p className="font-mono text-[9px] tracking-[0.25em] text-white/40 mb-3">
                {t.presetLabel}
              </p>
              <div className="space-y-2">
                {t.presets.map((msg, i) => (
                  <button
                    key={i}
                    onClick={() => handlePreset(msg)}
                    data-testid={`whatsapp-preset-${i}`}
                    className="w-full text-left border border-white/10 hover:border-[#25D366] bg-transparent hover:bg-[#25D366]/5 text-white/85 hover:text-white text-sm px-3 py-2.5 transition-colors"
                  >
                    {msg}
                  </button>
                ))}
              </div>
            </div>

            {/* CTA */}
            <a
              href={buildWaLink(t.greet)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-cta-full"
              className="mt-5 flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#1EBE5D] text-white font-mono text-[11px] tracking-[0.25em] px-5 py-3.5 transition-colors relative"
            >
              <WhatsAppIcon size={16} />
              {t.cta}
            </a>

            <p className="font-mono text-[9px] tracking-[0.2em] text-white/30 mt-4 text-center">
              {PHONE_DISPLAY}
            </p>
          </div>
        </div>
      )}

      {/* Floating trigger */}
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="whatsapp-button"
        aria-label={t.open}
        className={`fixed bottom-4 right-4 md:bottom-8 md:right-8 z-[59] h-14 w-14 md:h-16 md:w-16 rounded-full bg-[#25D366] hover:bg-[#1EBE5D] text-white shadow-2xl flex items-center justify-center transition-all duration-300 ${
          open ? "scale-90" : "scale-100 hover:scale-110"
        }`}
      >
        <span
          className={`absolute inset-0 rounded-full bg-[#25D366] ${
            open ? "" : "animate-ping opacity-30"
          }`}
        />
        <span className="relative">
          {open ? <X size={26} /> : <WhatsAppIcon size={28} />}
        </span>
      </button>
    </>
  );
};

export default WhatsAppChat;
